import React from 'react';
import { Label } from '@/components/ui/label';
import NumberInput from '@/components/ui/NumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AdicionalCalculo, BaseCalculo, BasesCalculo, ModoCalculo, TipoMoneda } from '@/types/costo';
import { BASES_CALCULO_VACIAS, ETIQUETA_BASE_CALCULO } from './calculo';

interface CamposCalculoProps {
  modo: ModoCalculo;
  onModoChange: (modo: ModoCalculo) => void;
  /** Base sobre la que se calcula el porcentaje. */
  baseCalculo: BaseCalculo;
  onBaseCalculoChange: (base: BaseCalculo) => void;
  porcentaje: string;
  onPorcentajeChange: (valor: string) => void;
  /** Qué se hace con el resultado del porcentaje: sumar una suma fija o aplicar otro porcentaje. */
  adicional: AdicionalCalculo;
  onAdicionalChange: (adicional: AdicionalCalculo) => void;
  /** Suma fija que se agrega al porcentaje. */
  sumaFija: string;
  onSumaFijaChange: (valor: string) => void;
  /** Moneda de la suma fija: si no es la del ítem, se convierte con la cotización. */
  monedaSumaFija: TipoMoneda;
  onMonedaSumaFijaChange: (moneda: TipoMoneda) => void;
  /** Porcentaje que se aplica al resultado del porcentaje. */
  porcentajeAdicional: string;
  onPorcentajeAdicionalChange: (valor: string) => void;
  disabled?: boolean;
  /** Moneda del ítem: una base en otra moneda se convierte con la cotización. */
  moneda: TipoMoneda;
  /** Cotización del dólar del presupuesto (ARS por USD). */
  cotizacionDolar?: number | null;
  /** Moneda de cada base del presupuesto. */
  bases?: BasesCalculo;
  idPrefix?: string;
}

type OpcionCalculo = 'fijo' | BaseCalculo;

const OPCIONES: { value: OpcionCalculo; label: string }[] = [
  { value: 'fijo', label: 'Monto fijo' },
  { value: 'base_imponible', label: '% de la base imponible' },
  { value: 'monto_venta', label: '% del monto de venta' },
];

/**
 * Selector de cálculo de un ítem: monto fijo, o porcentaje de la base imponible
 * o del monto de venta más una suma fija opcional o con otro porcentaje aplicado
 * al resultado (ej.: IVA = 21% del 2% de la base). Una base o una suma fija en
 * otra moneda que el ítem se convierte con la cotización del dólar. El monto
 * fijo lo edita quien usa el componente.
 */
export const CamposCalculo: React.FC<CamposCalculoProps> = ({
  modo,
  onModoChange,
  baseCalculo,
  onBaseCalculoChange,
  porcentaje,
  onPorcentajeChange,
  adicional,
  onAdicionalChange,
  sumaFija,
  onSumaFijaChange,
  monedaSumaFija,
  onMonedaSumaFijaChange,
  porcentajeAdicional,
  onPorcentajeAdicionalChange,
  disabled = false,
  moneda,
  cotizacionDolar = null,
  bases = BASES_CALCULO_VACIAS,
  idPrefix = 'calculo',
}) => {
  const seleccionada: OpcionCalculo = modo === 'fijo' ? 'fijo' : baseCalculo;
  const baseEnOtraMoneda = modo === 'porcentaje' && bases[baseCalculo].moneda !== moneda;
  const sumaFijaEnOtraMoneda = modo === 'porcentaje' && adicional === 'suma_fija' && monedaSumaFija !== moneda;

  const seleccionar = (opcion: OpcionCalculo) => {
    if (opcion === 'fijo') {
      onModoChange('fijo');
      return;
    }

    onBaseCalculoChange(opcion);
    onModoChange('porcentaje');
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Cálculo</Label>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {OPCIONES.map(({ value, label }) => {
            return (
              <label
                key={value}
                className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              >
                <input
                  type="radio"
                  name={`${idPrefix}-modo`}
                  value={value}
                  checked={seleccionada === value}
                  onChange={() => seleccionar(value)}
                  disabled={disabled}
                  className="sr-only"
                />
                <div
                  className={`flex h-4 w-4 items-center justify-center rounded-full border-2 transition-colors ${
                    seleccionada === value ? 'border-primary' : 'border-muted-foreground/40'
                  }`}
                >
                  {seleccionada === value && <div className="h-2 w-2 rounded-full bg-primary" />}
                </div>
                <span
                  className={`text-sm font-medium transition-colors ${
                    seleccionada === value ? 'text-primary' : 'text-foreground'
                  }`}
                >
                  {label}
                </span>
              </label>
            );
          })}
        </div>
        {baseEnOtraMoneda && cotizacionDolar ? (
          <p className="text-xs text-muted-foreground">
            {`${baseCalculo === 'monto_venta' ? 'El' : 'La'} ${ETIQUETA_BASE_CALCULO[baseCalculo]} está en ${bases[baseCalculo].moneda}: se convierte a ${moneda} con la cotización del dólar.`}
          </p>
        ) : null}
        {(baseEnOtraMoneda || sumaFijaEnOtraMoneda) && !cotizacionDolar && (
          <p className="text-xs text-amber-700">
            Falta la cotización del dólar: por ahora no cuenta lo que está en otra moneda.
          </p>
        )}
      </div>

      {modo === 'porcentaje' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-porcentaje`}>Porcentaje *</Label>
            <NumberInput
              id={`${idPrefix}-porcentaje`}
              decimals={4}
              min={0}
              max={100}
              value={porcentaje}
              onChange={onPorcentajeChange}
              placeholder="Ej: 1.5"
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-adicional`}>Sobre el resultado</Label>
            <Select value={adicional} onValueChange={onAdicionalChange} disabled={disabled}>
              <SelectTrigger id={`${idPrefix}-adicional`} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="suma_fija">Sumar una suma fija</SelectItem>
                <SelectItem value="porcentaje">Aplicar un porcentaje</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {adicional === 'suma_fija' ? (
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor={`${idPrefix}-suma-fija`}>Suma fija</Label>
              <div className="flex gap-2">
                <NumberInput
                  id={`${idPrefix}-suma-fija`}
                  currency
                  decimals={2}
                  min={0}
                  value={sumaFija}
                  onChange={onSumaFijaChange}
                  placeholder="0.00"
                  disabled={disabled}
                  containerClassName="flex-1"
                />
                <Select value={monedaSumaFija} onValueChange={onMonedaSumaFijaChange} disabled={disabled}>
                  <SelectTrigger className="w-24" aria-label="Moneda de la suma fija">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ARS">ARS</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {sumaFijaEnOtraMoneda && cotizacionDolar ? (
                <p className="text-xs text-muted-foreground">
                  {`La suma fija está en ${monedaSumaFija}: se convierte a ${moneda} con la cotización del dólar.`}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-porcentaje-adicional`}>Porcentaje sobre el resultado *</Label>
              <NumberInput
                id={`${idPrefix}-porcentaje-adicional`}
                decimals={4}
                min={0}
                max={100}
                value={porcentajeAdicional}
                onChange={onPorcentajeAdicionalChange}
                placeholder="Ej: 21"
                disabled={disabled}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
